import { DateTime } from "luxon";
import { timezoneUtils } from "@/lib/timezone";

/**
 * Unified appointment interface for calendar utilities
 */
export interface BaseAppointment {
  id: string;
  startTime: string | DateTime;
  duration?: number; // in minutes
  bookingStatus?: string; // Booking status
  outcome?: string | null; // Outcome field
  workerId?: string | null;
}

/**
 * Type guard to check if appointment has duration
 */
function hasDuration(appointment: any): appointment is { duration: number } {
  return typeof appointment.duration === "number";
}

/**
 * Get duration from appointment (handles both types)
 */
function getAppointmentDuration(appointment: any): number {
  if (hasDuration(appointment)) {
    return appointment.duration;
  }
  // Fallback: try to calculate from endTime or default to 60 minutes
  if (appointment.endTime) {
    const start = getAppointmentStartTime(appointment);
    const end = getAppointmentEndTime(appointment);
    return Math.round(end.diff(start, "minutes").minutes);
  }
  return 60; // Default duration
}

/**
 * Get start time as DateTime object in salon timezone
 */
function getAppointmentStartTime(appointment: any): DateTime {
  let dateTime: DateTime;

  if (DateTime.isDateTime(appointment.startTime)) {
    dateTime = appointment.startTime;
  } else {
    dateTime = timezoneUtils.fromISO(appointment.startTime);
  }

  return dateTime;
}

/**
 * Get end time as DateTime object in salon timezone
 */
function getAppointmentEndTime(appointment: any): DateTime {
  let dateTime: DateTime;

  if (appointment.endTime) {
    if (DateTime.isDateTime(appointment.endTime)) {
      dateTime = appointment.endTime;
    } else {
      dateTime = timezoneUtils.fromISO(appointment.endTime);
    }
  } else {
    // Calculate end time from start time and duration
    const startTime = getAppointmentStartTime(appointment);
    const duration = getAppointmentDuration(appointment);
    dateTime = startTime.plus({ minutes: duration });
  }

  return dateTime;
}

/**
 * Configuration for time slot generation
 */
export interface TimeSlotConfig {
  startHour: number;
  endHour: number;
  hourHeight: number;
  showMinutes?: boolean;
  format12Hour?: boolean;
}

/**
 * Configuration for appointment positioning
 */
export interface PositionConfig {
  hourHeight: number;
  minHeight?: number;
}

/**
 * Result of overlap adjustment calculation
 */
export interface OverlapAdjustment {
  width: string;
  left: string;
  hasOverlap: boolean;
  overlapCount: number;
}

/**
 * Appointment position calculation result
 */
export interface AppointmentPosition {
  top: number;
  height: number;
}

/**
 * Filter appointments by status and worker
 */
export function filterAppointments<T extends BaseAppointment>(
  appointments: T[],
  statusFilters: Record<string, boolean> | string[],
  workerFilters: Record<string, boolean> | string[]
): T[] {
  return appointments.filter((appointment) => {
    // Get the booking status to check
    const statusToCheck = appointment.bookingStatus;

    // Handle both array and object filter formats
    const statusMatch = Array.isArray(statusFilters)
      ? statusFilters.length === 0 ||
        (statusToCheck !== undefined && statusFilters.includes(statusToCheck))
      : statusToCheck !== undefined
        ? (statusFilters[statusToCheck] ?? true)
        : true;

    const workerMatch = Array.isArray(workerFilters)
      ? workerFilters.length === 0 ||
        !appointment.workerId ||
        workerFilters.includes(appointment.workerId)
      : appointment.workerId
        ? (workerFilters[appointment.workerId] ?? true)
        : true;

    return statusMatch && workerMatch;
  });
}

/**
 * Filter appointments for a specific date
 */
export function filterAppointmentsForDate<T extends BaseAppointment>(
  appointments: T[],
  targetDate: DateTime,
  statusFilters: Record<string, boolean> | string[],
  workerFilters: Record<string, boolean> | string[]
): T[] {
  const filteredByFilters = filterAppointments(appointments, statusFilters, workerFilters);

  const targetDateTime = targetDate;

  return filteredByFilters.filter((appointment) => {
    const appointmentDate = getAppointmentStartTime(appointment);
    return appointmentDate.hasSame(targetDateTime, "day");
  });
}

/**
 * Generate time slots array for calendar display
 */
export function generateTimeSlots(config: TimeSlotConfig): string[] {
  const { startHour, endHour, showMinutes = true, format12Hour = false } = config;
  const slots: string[] = [];

  const baseDate = DateTime.now().startOf("day");

  for (let hour = startHour; hour < endHour; hour++) {
    const dateTime = baseDate.set({ hour, minute: 0 });
    const formatString = format12Hour
      ? showMinutes
        ? "h:mm a"
        : "h a"
      : showMinutes
        ? "HH:mm"
        : "HH";
    const timeString = dateTime.toFormat(formatString);
    slots.push(timeString);
  }

  return slots;
}

/**
 * Calculate appointment position within time slot grid
 */
export function calculateAppointmentPosition<T extends BaseAppointment>(
  appointment: T,
  config: PositionConfig,
  startHour: number = 0
): AppointmentPosition {
  const { hourHeight, minHeight = 24 } = config;
  const startTime = getAppointmentStartTime(appointment);
  const duration = getAppointmentDuration(appointment);
  const hour = startTime.hour - startHour;
  const minute = startTime.minute;

  const top = hour * hourHeight + (minute / 60) * hourHeight;
  const height = Math.max((duration / 60) * hourHeight, minHeight);

  return { top, height };
}

/**
 * Google Calendar-style appointment layout information
 */
export interface AppointmentLayout {
  width: string;
  left: string;
  hasOverlap: boolean;
  overlapCount: number;
  zIndex: number;
}

/**
 * Cache for layout maps to prevent redundant calculations
 */
const layoutMapCache = new Map<string, Map<string, AppointmentLayout>>();

/**
 * Global overlap group processing interface
 */
interface OverlapGroup<T extends BaseAppointment> {
  appointments: T[];
  columns: T[][];
  startTime: number;
  endTime: number;
}

/**
 * Maximum number of columns before stacking appointments
 */
const MAX_COLUMNS = 10;

/**
 * Transitive overlap resolution algorithm using BFS to compute connected components
 * Ensures each appointment appears in exactly one group
 */
function resolveGlobalOverlaps<T extends BaseAppointment>(
  appointments: T[],
  positionConfig: PositionConfig,
  startHour: number = 0
): OverlapGroup<T>[] {
  const groups: OverlapGroup<T>[] = [];
  const assigned = new Set<string>();
  const EPS = 2; // px buffer to avoid 1px visual seams

  const overlaps = (a: T, b: T) => {
    const { top: aTop, height: aH } = calculateAppointmentPosition(a, positionConfig, startHour);
    const { top: bTop, height: bH } = calculateAppointmentPosition(b, positionConfig, startHour);
    const aBottom = aTop + aH;
    const bBottom = bTop + bH;
    return aTop < bBottom - EPS && aBottom - EPS > bTop;
  };

  for (const seed of appointments) {
    if (assigned.has(seed.id)) continue;

    // --- BFS to get the full transitive group ---
    const queue: T[] = [seed];
    const groupSet = new Set<string>([seed.id]);
    const group: T[] = [seed];
    assigned.add(seed.id);

    while (queue.length) {
      const cur = queue.shift();
      if (!cur) continue;
      for (const other of appointments) {
        if (groupSet.has(other.id)) continue;
        if (overlaps(cur, other)) {
          groupSet.add(other.id);
          assigned.add(other.id);
          queue.push(other);
          group.push(other);
        }
      }
    }

    // --- Column assignment (interval partitioning) ---
    const sorted = group.sort((a, b) => {
      const ta = getAppointmentStartTime(a).toMillis();
      const tb = getAppointmentStartTime(b).toMillis();
      if (ta !== tb) return ta - tb;
      return getAppointmentDuration(b) - getAppointmentDuration(a); // longer first if tie
    });

    const columns: T[][] = [];
    for (const apt of sorted) {
      const { top: s } = calculateAppointmentPosition(apt, positionConfig, startHour);
      let placed = false;

      // try to place into the first non-conflicting column
      for (let i = 0; i < Math.min(columns.length, MAX_COLUMNS); i++) {
        const col = columns[i];
        const last = col[col.length - 1];
        const { top: ls, height: lh } = calculateAppointmentPosition(
          last,
          positionConfig,
          startHour
        );
        const le = ls + lh;
        if (le + EPS <= s) {
          // no vertical overlap
          col.push(apt);
          placed = true;
          break;
        }
      }

      if (!placed) {
        if (columns.length < MAX_COLUMNS) {
          columns.push([apt]);
        } else {
          // hard cap: stack into the last column if we exceed MAX_COLUMNS
          columns[MAX_COLUMNS - 1].push(apt);
        }
      }
    }

    const groupStart = Math.min(
      ...sorted.map((a) => calculateAppointmentPosition(a, positionConfig, startHour).top)
    );
    const groupEnd = Math.max(
      ...sorted.map((a) => {
        const { top, height } = calculateAppointmentPosition(a, positionConfig, startHour);
        return top + height;
      })
    );

    groups.push({ appointments: sorted, columns, startTime: groupStart, endTime: groupEnd });
  }

  return groups;
}

/**
 * Compute global appointment layout map (Google Calendar style)
 * Processes all appointments at once to ensure consistent, non-overlapping positioning
 */
export function computeAppointmentLayoutMap<T extends BaseAppointment>(
  allAppointments: T[],
  config: PositionConfig,
  startHour: number = 0
): Map<string, AppointmentLayout> {
  // Better cache key: include end/duration
  const cacheKey = [
    ...allAppointments
      .map((apt) => {
        const s = getAppointmentStartTime(apt).toFormat("HHmm");
        const e = getAppointmentEndTime(apt).toFormat("HHmm");
        return `${apt.id}-${s}-${e}`;
      })
      .sort(),
    `h${config.hourHeight}`,
    `s${startHour}`,
  ].join("|");

  const cached = layoutMapCache.get(cacheKey);
  if (cached) return cached;

  const groups = resolveGlobalOverlaps(allAppointments, config, startHour);
  const layoutMap = new Map<string, AppointmentLayout>();

  for (const group of groups) {
    const totalColumns = group.columns.length;
    const gapPx = 3;
    const columnWidthPct = 100 / totalColumns;

    for (let colIndex = 0; colIndex < totalColumns; colIndex++) {
      for (const apt of group.columns[colIndex]) {
        const leftPct = colIndex * columnWidthPct;

        layoutMap.set(apt.id, {
          width: `calc(${columnWidthPct}% - ${gapPx * 2}px)`,
          left: `calc(${leftPct}% + ${gapPx}px)`,
          hasOverlap: totalColumns > 1,
          overlapCount: totalColumns,
          zIndex: 10 + colIndex, // optional
        });
      }
    }
  }

  layoutMapCache.set(cacheKey, layoutMap);
  if (layoutMapCache.size > 100) {
    const firstKey = layoutMapCache.keys().next().value;
    layoutMapCache.delete(firstKey);
  }
  return layoutMap;
}

// ... rest of utility functions remain the same ...

/**
 * Get consistent status color classes
 */
export function getStatusColor(status: string, isSuggestionContext: boolean = false): string {
  switch (status) {
    // Suggestion statuses - high visual priority
    case "suggested-ok":
      return "bg-green-100 border-green-300 text-green-800";
    case "suggested-warning":
      return "bg-yellow-100 border-yellow-300 text-yellow-800";
    case "suggested-error":
      return "bg-red-100 border-red-300 text-red-800";

    // Regular appointment statuses
    case "CONFIRMED":
    case "confirmed":
    case "accepted":
      return isSuggestionContext
        ? "bg-gray-100 border-gray-300 text-gray-800"
        : "bg-green-100 border-green-300 text-green-800";
    case "PENDING":
    case "pending":
    case "waiting":
      return isSuggestionContext
        ? "bg-amber-100 border-amber-300 text-amber-800"
        : "bg-yellow-100 border-yellow-300 text-yellow-800";
    case "DECLINED":
    case "cancelled":
    case "declined":
      return isSuggestionContext
        ? "bg-gray-100 border-gray-300 text-gray-800"
        : "bg-red-100 border-red-300 text-red-800";
    case "COMPLETED":
    case "completed":
      return isSuggestionContext
        ? "bg-gray-100 border-gray-300 text-gray-800"
        : "bg-blue-100 border-blue-300 text-blue-800";
    case "IN_PROGRESS":
    case "in-progress":
      return isSuggestionContext
        ? "bg-gray-100 border-gray-300 text-gray-800"
        : "bg-purple-100 border-purple-300 text-purple-800";
    case "NO_SHOW":
    case "no-show":
      return "bg-gray-100 border-gray-300 text-gray-800";
    case "RESCHEDULED":
    case "rescheduled":
      return isSuggestionContext
        ? "bg-gray-100 border-gray-300 text-gray-800"
        : "bg-orange-100 border-orange-300 text-orange-800";
    case "unassigned":
      return isSuggestionContext
        ? "bg-gray-100 border-gray-300 text-gray-800"
        : "bg-indigo-100 border-indigo-300 text-indigo-800";
    default:
      return "bg-gray-100 border-gray-300 text-gray-800";
  }
}

/**
 * Get status color for border-left style (used in some views)
 */
export function getStatusColorBorderLeft(
  status: string,
  isSuggestionContext: boolean = false
): string {
  switch (status) {
    // Suggestion statuses - high visual priority
    case "suggested-ok":
      return "bg-green-100 border-l-4 border-green-500 text-green-800";
    case "suggested-warning":
      return "bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800";
    case "suggested-error":
      return "bg-red-100 border-l-4 border-red-500 text-red-800";

    // Regular appointment statuses
    case "CONFIRMED":
    case "confirmed":
    case "accepted":
      return isSuggestionContext
        ? "bg-gray-100 border-l-4 border-gray-500 text-gray-800"
        : "bg-green-100 border-l-4 border-green-500 text-green-800";
    case "PENDING":
    case "pending":
    case "waiting":
      return isSuggestionContext
        ? "bg-amber-100 border-l-4 border-amber-500 text-amber-800"
        : "bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800";
    case "DECLINED":
    case "cancelled":
    case "declined":
      return isSuggestionContext
        ? "bg-gray-100 border-l-4 border-gray-500 text-gray-800"
        : "bg-red-100 border-l-4 border-red-500 text-red-800";
    case "COMPLETED":
    case "completed":
      return isSuggestionContext
        ? "bg-gray-100 border-l-4 border-gray-500 text-gray-800"
        : "bg-blue-100 border-l-4 border-blue-500 text-blue-800";
    case "IN_PROGRESS":
    case "in-progress":
      return isSuggestionContext
        ? "bg-gray-100 border-l-4 border-gray-500 text-gray-800"
        : "bg-purple-100 border-l-4 border-purple-500 text-purple-800";
    case "NO_SHOW":
    case "no-show":
      return "bg-gray-100 border-l-4 border-gray-500 text-gray-800";
    case "RESCHEDULED":
    case "rescheduled":
      return isSuggestionContext
        ? "bg-gray-100 border-l-4 border-gray-500 text-gray-800"
        : "bg-orange-100 border-l-4 border-orange-500 text-orange-800";
    case "unassigned":
      return isSuggestionContext
        ? "bg-gray-100 border-l-4 border-gray-500 text-gray-800"
        : "bg-indigo-100 border-l-4 border-indigo-500 text-indigo-800";
    default:
      return "bg-gray-100 border-l-4 border-gray-500 text-gray-800";
  }
}

/**
 * Get simple dot color for month view or minimal displays
 */
export function getStatusDotColor(status: string): string {
  switch (status) {
    case "CONFIRMED":
    case "confirmed":
    case "accepted":
      return "bg-green-500";
    case "PENDING":
    case "pending":
    case "waiting":
      return "bg-yellow-500";
    case "DECLINED":
    case "cancelled":
    case "declined":
      return "bg-red-500";
    case "COMPLETED":
    case "completed":
      return "bg-blue-500";
    case "IN_PROGRESS":
    case "in-progress":
      return "bg-purple-500";
    case "NO_SHOW":
    case "no-show":
      return "bg-gray-500";
    case "RESCHEDULED":
    case "rescheduled":
      return "bg-orange-500";
    case "unassigned":
      return "bg-indigo-500";
    default:
      return "bg-gray-500";
  }
}

/**
 * Check if a date is today
 */
export function isToday(date: DateTime): boolean {
  const dateTime = date;
  const today = DateTime.now();
  return dateTime.hasSame(today, "day");
}

/**
 * Check if a date is in the current month
 */
export function isCurrentMonth(date: DateTime, referenceDate: DateTime): boolean {
  const dateTime = date;
  const refDateTime = referenceDate;

  return dateTime.hasSame(refDateTime, "month");
}

/**
 * Format time range for appointment display
 */
export function formatAppointmentTimeRange<T extends BaseAppointment>(appointment: T): string {
  const startTime = getAppointmentStartTime(appointment);
  const duration = getAppointmentDuration(appointment);

  const startTimeString = startTime.toFormat("HH:mm");
  const endTime = startTime.plus({ minutes: duration });
  const endTimeString = endTime.toFormat("HH:mm");

  return `${startTimeString} - ${endTimeString} (${duration}min)`;
}

/**
 * Calculate 15-minute interval from mouse position within time slot
 */
export function calculate15MinuteInterval(relativeY: number, slotHeight: number): number {
  return Math.floor((relativeY / slotHeight) * 4); // 4 intervals per hour
}

/**
 * Format date for day header display
 */
export function formatDayHeader(date: DateTime): { weekday: string; dayNumber: string } {
  const dateTime = date;

  return {
    weekday: dateTime.toFormat("EEE"),
    dayNumber: dateTime.day.toString(),
  };
}

/**
 * Generate date range for multi-day views
 */
export function generateDateRange(startDate: DateTime, numberOfDays: number): DateTime[] {
  const startDateTime = startDate;

  return Array.from({ length: numberOfDays }, (_, i) => {
    return startDateTime.plus({ days: i });
  });
}

/**
 * Get week start date (Sunday)
 */
export function getWeekStart(date: DateTime): DateTime {
  const dateTime = date;

  return dateTime.startOf("week");
}

/**
 * Get month calendar grid (42 days - 6 weeks)
 */
export function getMonthCalendarGrid(date: DateTime): DateTime[] {
  const dateTime = date;

  const startOfMonth = dateTime.startOf("month");
  const startOfCalendar = startOfMonth.startOf("week");

  const calendarDays: DateTime[] = [];
  let current = startOfCalendar;

  for (let i = 0; i < 42; i++) {
    calendarDays.push(current);
    current = current.plus({ days: 1 });
  }

  return calendarDays;
}

/**
 * Calculate optimized time range based on operating hours
 * Applies -3/+3 hour padding to operating hours, fallback to 6 AM - 11 PM
 * @deprecated Use calculateExtendedTimeRange for appointment-aware time range calculation
 */
export function calculateOptimizedTimeRange(
  dayOperatingHours: Array<{ openTime?: string; closeTime?: string }>
): { startHour: number; endHour: number } {
  // Default fallback range
  const defaultStart = 6; // 6 AM
  const defaultEnd = 23; // 11 PM

  // If we have operating hours, use them with -3/+3 hour padding
  if (dayOperatingHours.length > 0) {
    const validOpenTimes: number[] = [];
    const validCloseTimes: number[] = [];

    dayOperatingHours.forEach((hours) => {
      // Safely parse opening time
      if (hours.openTime) {
        try {
          const openTime = DateTime.fromISO(hours.openTime, { zone: "local" });
          if (openTime.isValid && !Number.isNaN(openTime.hour)) {
            validOpenTimes.push(openTime.hour);
          }
        } catch (_e) {
          console.warn("Invalid opening time:", hours.openTime);
        }
      }

      // Safely parse closing time
      if (hours.closeTime) {
        try {
          const closeTime = DateTime.fromISO(hours.closeTime, { zone: "local" });
          if (closeTime.isValid && !Number.isNaN(closeTime.hour)) {
            validCloseTimes.push(closeTime.hour);
          }
        } catch (_e) {
          console.warn("Invalid closing time:", hours.closeTime);
        }
      }
    });

    if (validOpenTimes.length > 0 && validCloseTimes.length > 0) {
      const earliestOpen = Math.min(...validOpenTimes);
      const latestClose = Math.max(...validCloseTimes);

      // Apply -3/+3 hour padding, but keep within 0-24 range
      const optimizedStart = Math.max(0, earliestOpen - 3);
      const optimizedEnd = Math.min(24, latestClose + 3);

      return {
        startHour: optimizedStart,
        endHour: optimizedEnd,
      };
    }
  }

  return {
    startHour: defaultStart,
    endHour: defaultEnd,
  };
}

/**
 * Calculate extended time range based on operating hours AND appointments
 * Ensures all appointments are visible by extending the time range when needed
 */
export function calculateExtendedTimeRange<T extends BaseAppointment>(
  dayOperatingHours: Array<{ openTime?: string; closeTime?: string }>,
  appointments: T[] = [],
  minPadding: number = 1
): { startHour: number; endHour: number } {
  // Start with the base range from operating hours
  const baseRange = calculateOptimizedTimeRange(dayOperatingHours);
  const { startHour, endHour } = baseRange;

  // If no appointments, return the base range
  if (appointments.length === 0) {
    return { startHour, endHour };
  }

  // Find the earliest and latest appointment times
  let earliestHour = startHour;
  let latestHour = endHour;

  appointments.forEach((appointment) => {
    try {
      const startTime = getAppointmentStartTime(appointment);
      const endTime = getAppointmentEndTime(appointment);

      if (startTime.isValid && endTime.isValid) {
        const appointmentStartHour = startTime.hour + startTime.minute / 60;
        const appointmentEndHour = endTime.hour + endTime.minute / 60;

        // Track the earliest start and latest end
        if (appointmentStartHour < earliestHour) {
          earliestHour = appointmentStartHour;
        }
        if (appointmentEndHour > latestHour) {
          latestHour = appointmentEndHour;
        }
      }
    } catch (error) {
      console.warn("Error processing appointment time for calendar range:", error);
    }
  });

  // Apply padding to the extended range
  const extendedStartHour = Math.max(0, Math.floor(earliestHour) - minPadding);
  const extendedEndHour = Math.min(24, Math.ceil(latestHour) + minPadding);

  // Use the wider of the two ranges (base vs extended)
  const finalStartHour = Math.min(startHour, extendedStartHour);
  const finalEndHour = Math.max(endHour, extendedEndHour);

  return {
    startHour: finalStartHour,
    endHour: finalEndHour,
  };
}
